import { existsSync } from "node:fs";
import { homedir } from "node:os";
import { dirname, join, resolve } from "node:path";

import { parseJsonObject, readTextIfExists } from "./fs.js";
import {
  inspectOpenCodePlugin,
  openCodePluginPathsForHome,
} from "./opencode-plugin-config.js";

export const CLIENT_CONTINUITY_SCHEMA_VERSION = "orgx-client-continuity/v2";

export type ContinuityClient = "claude-code" | "codex" | "cursor" | "opencode";
export type ContinuityClientState = "ready" | "attention_required" | "not_installed";

export interface ClientContinuityPaths {
  cursorManifestPath: string;
  cursorHooksPath: string;
  openCodeConfigPath: string;
  openCodeConfigJsoncPath: string;
  openCodePackageSpecCacheRoot: string;
  openCodePackageCachePath: string;
  openCodePackagePath: string;
}

export interface ClientContinuityStatus {
  client: ContinuityClient;
  state: ContinuityClientState;
  installed: boolean;
  adapterVersion: string | null;
  captureReady: boolean;
  firstTurnContextReady: boolean;
  workContextReady: boolean;
  receiptReady: boolean;
  terminalBoundary: string;
  warnings: string[];
  nextAction: string | null;
}

export interface ClientContinuityInspection {
  schemaVersion: typeof CLIENT_CONTINUITY_SCHEMA_VERSION;
  state: "ready" | "attention_required";
  clients: ClientContinuityStatus[];
}

export interface InspectClientContinuityOptions {
  paths?: Partial<ClientContinuityPaths>;
  shared: {
    automaticDelivery: boolean;
    claudeCodeInstalled: boolean;
    codexInstalled: boolean;
    summaryHookInstalled: boolean;
    summaryHookStatus?: "current" | "stale" | "missing";
    summaryHookFirstTurnContextCapable?: boolean;
    claudeCodeSummaryHooksInstalled?: boolean;
    codexSummaryHooksInstalled?: boolean;
    workContextReady: boolean;
  };
}

function summaryHookWarning(options: InspectClientContinuityOptions): string[] {
  if (options.shared.summaryHookStatus === "stale") {
    return ["The shared bounded session-summary hook is stale."];
  }
  if (options.shared.summaryHookInstalled) return [];
  return ["The shared bounded session-summary hook is missing."];
}

function readJson(path: string): Record<string, unknown> {
  return parseJsonObject(readTextIfExists(path));
}

function string(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

interface OpenCodePackageResolution {
  configured: boolean;
  packagePath: string | null;
  warning: string | null;
}

function configuredOpenCodePackagePath(
  paths: ClientContinuityPaths,
  explicitPackagePath: string | undefined,
): OpenCodePackageResolution {
  if (explicitPackagePath) {
    return { configured: false, packagePath: explicitPackagePath, warning: null };
  }

  const inspection = inspectOpenCodePlugin({
    configPaths: [paths.openCodeConfigPath, paths.openCodeConfigJsoncPath],
    packageSpecCacheRoot: paths.openCodePackageSpecCacheRoot,
    packagePaths: [paths.openCodePackageCachePath, paths.openCodePackagePath],
  });
  return {
    configured: inspection.configured,
    packagePath: inspection.packagePath,
    warning: inspection.problem ?? inspection.runtimeWarning,
  };
}

function sharedCaptureReady(options: InspectClientContinuityOptions): boolean {
  return options.shared.summaryHookInstalled && options.shared.automaticDelivery;
}

function sharedNextAction(options: InspectClientContinuityOptions): string | null {
  return sharedCaptureReady(options)
    ? null
    : "Run orgx-wizard hooks install --targets all, then re-run hooks doctor --json.";
}

function nativeSummaryHooksInstalled(
  client: "claude-code" | "codex",
  options: InspectClientContinuityOptions,
): boolean {
  const configured = client === "claude-code"
    ? options.shared.claudeCodeSummaryHooksInstalled
    : options.shared.codexSummaryHooksInstalled;
  return configured ?? true;
}

function contextNextAction(options: InspectClientContinuityOptions): string | null {
  return options.shared.workContextReady
    ? null
    : "Run orgx-wizard sessions context set --file <context.json> --cwd \"$PWD\", then start the client session.";
}

function receiptState(
  installed: boolean,
  receiptReady: boolean,
): ContinuityClientState {
  return receiptReady
    ? "ready"
    : installed
      ? "attention_required"
      : "not_installed";
}

function wizardHookStatus(
  client: "claude-code" | "codex",
  installed: boolean,
  options: InspectClientContinuityOptions,
): ClientContinuityStatus {
  const summaryHooksInstalled = nativeSummaryHooksInstalled(client, options);
  const firstTurnCapable = options.shared.summaryHookFirstTurnContextCapable ?? true;
  const captureReady = installed && summaryHooksInstalled && sharedCaptureReady(options);
  const firstTurnContextReady =
    installed &&
    summaryHooksInstalled &&
    options.shared.summaryHookInstalled &&
    firstTurnCapable &&
    options.shared.workContextReady;
  const receiptReady = captureReady && firstTurnContextReady;
  const warnings = [
    ...(!installed ? [`${client} runtime hooks are not installed.`] : []),
    ...(installed && !summaryHooksInstalled
      ? [`${client} is missing one or more bounded summary hook commands.`]
      : []),
    ...summaryHookWarning(options),
    ...(options.shared.summaryHookInstalled && !firstTurnCapable
      ? ["The installed shared hook is collector-only and cannot inject first-turn context."]
      : []),
    ...(!options.shared.automaticDelivery ? ["ACK-gated automatic delivery is not configured."] : []),
    ...(!options.shared.workContextReady ? ["No current, exact-directory session work context is active."] : []),
  ];
  return {
    client,
    state: receiptState(installed, receiptReady),
    installed,
    adapterVersion: null,
    captureReady,
    firstTurnContextReady,
    workContextReady: options.shared.workContextReady,
    receiptReady,
    terminalBoundary: "session_end",
    warnings,
    nextAction: receiptReady
      ? null
      : !installed
        ? `Run orgx-wizard hooks install --targets ${client}, then re-run hooks doctor --json.`
        : !summaryHooksInstalled || !options.shared.summaryHookInstalled || !firstTurnCapable
          ? `Run orgx-wizard hooks install --targets ${client}, then re-run hooks doctor --json.`
          : !options.shared.automaticDelivery
            ? sharedNextAction(options)
            : contextNextAction(options),
  };
}

function cursorStatus(
  paths: ClientContinuityPaths,
  options: InspectClientContinuityOptions,
): ClientContinuityStatus {
  const manifest = readJson(paths.cursorManifestPath);
  const hooks = readJson(paths.cursorHooksPath);
  const hookMap = hooks.hooks && typeof hooks.hooks === "object" && !Array.isArray(hooks.hooks)
    ? hooks.hooks as Record<string, unknown>
    : {};
  const installed = existsSync(paths.cursorManifestPath) && existsSync(paths.cursorHooksPath);
  const hasRunBoundary = ["afterAgentResponse", "stop", "sessionEnd"].every((name) =>
    Array.isArray(hookMap[name]) && hookMap[name].length > 0
  );
  const captureReady = installed && hasRunBoundary && sharedCaptureReady(options);
  const firstTurnContextReady = installed && options.shared.workContextReady;
  const receiptReady = captureReady && firstTurnContextReady;
  const warnings = [
    ...(!installed ? ["The managed Cursor companion plugin is not installed."] : []),
    ...(installed && !hasRunBoundary ? ["Cursor is missing one or more terminal receipt hooks."] : []),
    ...summaryHookWarning(options),
    ...(!options.shared.automaticDelivery ? ["ACK-gated automatic delivery is not configured."] : []),
    ...(!options.shared.workContextReady ? ["No current, exact-directory session work context is active."] : []),
  ];
  return {
    client: "cursor",
    state: receiptState(installed, receiptReady),
    installed,
    adapterVersion: string(manifest.version),
    captureReady,
    firstTurnContextReady,
    workContextReady: options.shared.workContextReady,
    receiptReady,
    terminalBoundary: "stop/session_end (afterAgentResponse CLI fallback)",
    warnings,
    nextAction: receiptReady
      ? null
      : captureReady
        ? contextNextAction(options)
      : !installed || !hasRunBoundary
        ? "Run orgx-wizard plugins add cursor, reload Cursor, then re-run hooks doctor --json."
        : sharedNextAction(options),
  };
}

function openCodeStatus(
  paths: ClientContinuityPaths,
  options: InspectClientContinuityOptions,
): ClientContinuityStatus {
  const resolution = configuredOpenCodePackagePath(paths, options.paths?.openCodePackagePath);
  const packageJson = resolution.packagePath ? readJson(resolution.packagePath) : {};
  const packageName = string(packageJson.name);
  const entry = string(packageJson.main) ?? "dist/index.js";
  const entryPath = resolution.packagePath
    ? resolve(dirname(resolution.packagePath), entry)
    : "";
  const bridgePath = join(dirname(entryPath), "sessionSummaryBridge.js");
  const bridge = readTextIfExists(bridgePath);
  const installed = packageName === "@useorgx/orgx-opencode-plugin" && Boolean(entryPath) && existsSync(entryPath);
  const hasRunBoundary = Boolean(
    bridge?.includes("session.idle") && bridge.includes("RunEnd")
  );
  const captureReady = installed && hasRunBoundary && sharedCaptureReady(options);
  const firstTurnContextReady = installed && options.shared.workContextReady;
  const receiptReady = captureReady && firstTurnContextReady;
  const warnings = [
    ...(resolution.warning ? [resolution.warning] : []),
    ...(!installed && !resolution.warning ? ["The stable OrgX OpenCode plugin entry point is not installed."] : []),
    ...(installed && !hasRunBoundary ? ["OpenCode is missing the session.idle to RunEnd bridge."] : []),
    ...summaryHookWarning(options),
    ...(!options.shared.automaticDelivery ? ["ACK-gated automatic delivery is not configured."] : []),
    ...(!options.shared.workContextReady ? ["No current, exact-directory session work context is active."] : []),
  ];
  return {
    client: "opencode",
    state: receiptState(installed, receiptReady),
    installed,
    adapterVersion: installed ? string(packageJson.version) : null,
    captureReady,
    firstTurnContextReady,
    workContextReady: options.shared.workContextReady,
    receiptReady,
    terminalBoundary: "session.idle -> run_end",
    warnings,
    nextAction: receiptReady
      ? null
      : captureReady
        ? contextNextAction(options)
      : !installed || !hasRunBoundary
        ? resolution.configured
          ? "Start OpenCode once so it installs the configured OrgX alpha package, then re-run hooks doctor --json."
          : "Install the current @useorgx/orgx-opencode-plugin alpha package, then re-run hooks doctor --json."
        : sharedNextAction(options),
  };
}

export function clientContinuityPathsForHome(home = homedir()): ClientContinuityPaths {
  const openCode = openCodePluginPathsForHome(home);
  return {
    cursorManifestPath: join(
      home,
      ".cursor",
      "plugins",
      "local",
      "cursor-plugin",
      ".cursor-plugin",
      "plugin.json",
    ),
    cursorHooksPath: join(
      home,
      ".cursor",
      "plugins",
      "local",
      "cursor-plugin",
      "hooks",
      "hooks.json",
    ),
    openCodeConfigPath: openCode.configPaths[0]!,
    openCodeConfigJsoncPath: openCode.configPaths[1]!,
    openCodePackageSpecCacheRoot: openCode.packageSpecCacheRoot!,
    openCodePackageCachePath: openCode.packagePaths[0]!,
    openCodePackagePath: openCode.packagePaths[1]!,
  };
}

export function inspectClientContinuity(
  options: InspectClientContinuityOptions,
): ClientContinuityInspection {
  const paths = { ...clientContinuityPathsForHome(), ...options.paths };
  if (options.paths?.openCodeConfigPath && !options.paths.openCodePackagePath) {
    const configDir = dirname(options.paths.openCodeConfigPath);
    paths.openCodeConfigJsoncPath = options.paths.openCodeConfigPath;
    paths.openCodePackageSpecCacheRoot = join(
      dirname(dirname(configDir)),
      ".cache",
      "opencode",
      "packages",
    );
    paths.openCodePackageCachePath = join(
      dirname(dirname(configDir)),
      ".cache",
      "opencode",
      "node_modules",
      "@useorgx",
      "orgx-opencode-plugin",
      "package.json",
    );
    paths.openCodePackagePath = join(configDir, "plugins", "orgx-opencode-plugin", "package.json");
  }
  const clients = [
    wizardHookStatus("claude-code", options.shared.claudeCodeInstalled, options),
    wizardHookStatus("codex", options.shared.codexInstalled, options),
    cursorStatus(paths, options),
    openCodeStatus(paths, options),
  ];
  return {
    schemaVersion: CLIENT_CONTINUITY_SCHEMA_VERSION,
    state: clients.every((client) => client.receiptReady) ? "ready" : "attention_required",
    clients,
  };
}
